import React, { useState, useEffect } from 'react';
import { X, Calendar, User, FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { surveyAPI } from '../services/api';
import * as XLSX from 'xlsx';

interface SurveyResponse {
  id: number;
  survey_id: number;
  user_id: number;
  answers: Record<string, any>;
  completed: boolean;
  created_at: string;
  updated_at: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

interface Question {
  id: number;
  title: string;
  type: string;
  options: Array<{
    id: number;
    value: string;
    label: string;
  }>;
}

interface Survey {
  id: number;
  title: string;
  questions: Question[];
}

interface SurveyResultsModalProps {
  surveyId: number;
  surveyTitle: string;
  onClose: () => void;
}

const SurveyResultsModal: React.FC<SurveyResultsModalProps> = ({
  surveyId,
  surveyTitle,
  onClose
}) => {
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState<string>('');
  const [filterUser, setFilterUser] = useState<string>('');

  useEffect(() => {
    loadSurvey();
    loadResponses();
  }, [surveyId]);

  useEffect(() => {
    loadResponses();
  }, [filterDate, filterUser]);

  const loadSurvey = async () => {
    try {
      const response = await surveyAPI.getSurvey(surveyId);
      if (response.success && response.survey) {
        setSurvey(response.survey);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки опроса:', error);
    }
  };

  const loadResponses = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getSurveyResponses(surveyId);
      if (response.success) {
        let filtered = response.responses || [];

        // Фильтр по дате
        if (filterDate) {
          const filterDateObj = new Date(filterDate);
          filtered = filtered.filter((r: SurveyResponse) => {
            const responseDate = new Date(r.created_at);
            return responseDate.toDateString() === filterDateObj.toDateString();
          });
        }

        // Фильтр по пользователю
        if (filterUser) {
          const searchTerm = filterUser.toLowerCase();
          filtered = filtered.filter((r: SurveyResponse) => {
            const email = (r.email || '').toLowerCase();
            const firstName = (r.first_name || '').toLowerCase();
            const lastName = (r.last_name || '').toLowerCase();
            return email.includes(searchTerm) || 
                   firstName.includes(searchTerm) || 
                   lastName.includes(searchTerm);
          });
        }

        setResponses(filtered);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки результатов');
    } finally {
      setLoading(false);
    }
  };

  // Преобразует ключ вопроса (question_17) в ID вопроса (17)
  const getQuestionIdFromKey = (key: string): number | null => {
    const match = key.match(/question_(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  };

  // Получает текст вопроса по ID
  const getQuestionTitle = (questionId: number): string => {
    if (!survey) return `Вопрос ${questionId}`;
    const question = survey.questions.find(q => q.id === questionId);
    return question ? question.title : `Вопрос ${questionId}`;
  };

  // Получает текст варианта ответа по значению
  const getOptionLabel = (questionId: number, optionValue: string): string => {
    if (!survey) return optionValue;
    const question = survey.questions.find(q => q.id === questionId);
    if (!question) return optionValue;
    const option = question.options.find(opt => opt.value === optionValue);
    return option ? option.label : optionValue;
  };

  // Форматирует ответы в читабельный вид
  const formatAnswers = (answers: Record<string, any>): string => {
    if (!survey) {
      return Object.entries(answers).map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.join(', ')}`;
        }
        return `${key}: ${value}`;
      }).join('; ');
    }

    return Object.entries(answers)
      .map(([key, value]) => {
        const questionId = getQuestionIdFromKey(key);
        if (!questionId) return `${key}: ${value}`;

        const questionTitle = getQuestionTitle(questionId);
        
        if (Array.isArray(value)) {
          const labels = value.map((val: string) => {
            if (typeof val === 'string' && val.startsWith('option_')) {
              return getOptionLabel(questionId, val);
            }
            return val;
          });
          return `${questionTitle}: ${labels.join(', ')}`;
        } else if (typeof value === 'string' && value.startsWith('option_')) {
          return `${questionTitle}: ${getOptionLabel(questionId, value)}`;
        } else {
          // Текстовый ответ
          return `${questionTitle}: ${value}`;
        }
      })
      .join('; ');
  };

  // Форматирует ответы для Excel (каждый вопрос в отдельный столбец)
  const formatAnswersForExcel = (answers: Record<string, any>): Record<string, string> => {
    const result: Record<string, string> = {};
    
    if (!survey) {
      Object.entries(answers).forEach(([key, value]) => {
        result[key] = Array.isArray(value) ? value.join(', ') : String(value);
      });
      return result;
    }

    // Создаем столбец для каждого вопроса
    survey.questions.forEach((question) => {
      const key = `question_${question.id}`;
      const answerValue = answers[key];
      
      if (answerValue === undefined || answerValue === null) {
        result[question.title] = '';
      } else if (Array.isArray(answerValue)) {
        const labels = answerValue.map((val: string) => {
          if (typeof val === 'string' && val.startsWith('option_')) {
            return getOptionLabel(question.id, val);
          }
          return String(val);
        });
        result[question.title] = labels.join(', ');
      } else if (typeof answerValue === 'string' && answerValue.startsWith('option_')) {
        result[question.title] = getOptionLabel(question.id, answerValue);
      } else {
        result[question.title] = String(answerValue);
      }
    });

    return result;
  };

  const handleExportExcel = () => {
    if (responses.length === 0) {
      toast.error('Нет данных для экспорта');
      return;
    }

    if (!survey) {
      toast.error('Данные опроса не загружены');
      return;
    }

    // Подготовка данных для Excel
    const excelData = responses.map((response) => {
      const userName = response.first_name && response.last_name
        ? `${response.first_name} ${response.last_name}`
        : 'Не указано';
      
      // Базовые колонки
      const row: Record<string, any> = {
        'ID ответа': response.id,
        'ID пользователя': response.user_id,
        'Имя': userName,
        'Email': response.email || '',
        'Дата ответа': new Date(response.created_at).toLocaleString('ru-RU'),
        'Завершен': response.completed ? 'Да' : 'Нет'
      };

      // Добавляем ответы по столбцам (каждый вопрос в отдельный столбец)
      const answersColumns = formatAnswersForExcel(response.answers);
      Object.assign(row, answersColumns);

      return row;
    });

    // Создание рабочей книги
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Результаты опроса');

    // Настройка ширины колонок
    const colWidths: Array<{ wch: number }> = [
      { wch: 10 }, // ID ответа
      { wch: 12 }, // ID пользователя
      { wch: 20 }, // Имя
      { wch: 25 }, // Email
      { wch: 20 }, // Дата ответа
      { wch: 10 }  // Завершен
    ];
    
    // Добавляем ширину для каждого вопроса
    if (survey && survey.questions) {
      survey.questions.forEach(() => {
        colWidths.push({ wch: 30 }); // Ширина для столбца с ответом на вопрос
      });
    }
    
    ws['!cols'] = colWidths;

    // Генерация файла
    const fileName = `survey_${surveyId}_results_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);

    toast.success('Результаты экспортированы в Excel');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Результаты опроса</h2>
            <p className="text-gray-600 mt-1">{surveyTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Filters and Export */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Фильтр по дате"
              />
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Поиск по пользователю"
              />
            </div>
            {(filterDate || filterUser) && (
              <button
                onClick={() => {
                  setFilterDate('');
                  setFilterUser('');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Сбросить фильтры
              </button>
            )}
            <button
              onClick={handleExportExcel}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FileSpreadsheet className="h-5 w-5" />
              <span>Выгрузить Excel</span>
            </button>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Всего ответов: <strong>{responses.length}</strong>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : responses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Нет результатов опроса
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Пользователь
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Завершен
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Дата
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ответы
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {responses.map((response) => (
                    <tr key={response.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {response.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {response.first_name && response.last_name
                          ? `${response.first_name} ${response.last_name}`
                          : 'Не указано'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {response.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {response.completed ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Да
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Нет
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(response.created_at).toLocaleString('ru-RU')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-md">
                        <div className="truncate" title={formatAnswers(response.answers)}>
                          {formatAnswers(response.answers)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyResultsModal;

